// Typed fetch for /api. Error envelope: docs/04-architecture/api-contract.md §2.

export type ApiErrorKind =
  | "network"
  | "session-expired"
  | "permission-denied"
  | "not-found"
  | "conflict"
  | "validation"
  | "rate-limited"
  | "server"
  | "unknown";

export type ApiErrorDetails = Record<string, unknown>;

/** One error type for every failure, including a network drop (status 0). */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly kind: ApiErrorKind,
    readonly details?: ApiErrorDetails,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Safe to send again as-is: the request never reached a decision. */
  get retryable(): boolean {
    return this.kind === "network" || this.kind === "server" || this.kind === "rate-limited";
  }
}

function kindFor(status: number): ApiErrorKind {
  if (status === 401) return "session-expired";
  if (status === 403) return "permission-denied";
  if (status === 404) return "not-found";
  if (status === 409) return "conflict";
  if (status === 400 || status === 422 || status === 428) return "validation";
  if (status === 429) return "rate-limited";
  if (status >= 500) return "server";
  return "unknown";
}

type Envelope = {
  error?: { code?: string; message?: string; details?: ApiErrorDetails; requestId?: string };
};

// ponytail: module-level getter. One Clerk session per tab, so no context is needed.
let getAuthToken: (() => Promise<string | null>) | null = null;

export function setAuthTokenGetter(getter: (() => Promise<string | null>) | null): void {
  getAuthToken = getter;
}

/** The current Clerk token, for the rare request that cannot go through apiFetch (a download). */
export async function currentAuthToken(): Promise<string | null> {
  return getAuthToken ? getAuthToken() : null;
}

/** UUID for the POSTs marked IK in the API contract. Reuse it on retry. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

// An admin's read-only "view as user" session (POST /api/admin/impersonation). Kept per tab in
// sessionStorage, and sent on ordinary /api routes only, never on /api/admin.
const VIEW_AS_KEY = "hs.viewAs";

export function getViewAsSession(): string | null {
  try {
    return sessionStorage.getItem(VIEW_AS_KEY);
  } catch {
    return null;
  }
}

export function setViewAsSession(sessionId: string | null): void {
  try {
    if (sessionId) sessionStorage.setItem(VIEW_AS_KEY, sessionId);
    else sessionStorage.removeItem(VIEW_AS_KEY);
  } catch {
    // Private mode: the session simply does not survive a reload.
  }
}

export interface ApiRequest {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /** The version you read. Sent as If-Match on updates. */
  ifMatch?: number | string;
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function apiFetch<T>(path: string, req: ApiRequest = {}): Promise<T> {
  const headers = new Headers({ Accept: "application/json" });
  // A multipart upload (api-contract 6.10) must keep the Content-Type FormData generates for
  // itself: it carries a boundary only the browser can produce. Setting ours would break it.
  const form = typeof FormData !== "undefined" && req.body instanceof FormData ? req.body : null;
  if (req.body !== undefined && !form) headers.set("Content-Type", "application/json");
  if (req.ifMatch !== undefined) headers.set("If-Match", '"' + String(req.ifMatch) + '"');
  if (req.idempotencyKey) headers.set("Idempotency-Key", req.idempotencyKey);

  const token = getAuthToken ? await getAuthToken() : null;
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const viewAs = path.startsWith("/api/admin") ? null : getViewAsSession();
  if (viewAs) headers.set("X-Impersonation-Session", viewAs);

  let response: Response;
  try {
    response = await fetch(path, {
      method: req.method ?? "GET",
      headers,
      body: req.body === undefined ? undefined : (form ?? JSON.stringify(req.body)),
      signal: req.signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(
      0,
      "NETWORK",
      "Cannot reach HourSign. Check your connection and try again.",
      "network",
    );
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let parsed: unknown;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = undefined;
    }
  }

  if (!response.ok) {
    const envelope = (parsed ?? {}) as Envelope;
    throw new ApiError(
      response.status,
      envelope.error?.code ?? "UNKNOWN",
      envelope.error?.message ?? "Something went wrong. Please try again.",
      kindFor(response.status),
      envelope.error?.details,
      envelope.error?.requestId ?? response.headers.get("X-Request-Id") ?? undefined,
    );
  }

  return parsed as T;
}
